import { useCallback, useEffect, useState, type JSX } from 'react'
import AssetSelectionModal from '../../components/admin/channels/AssetSelectionModal.js'
import AddAccountModal from '../../components/admin/channels/AddAccountModal.js'
import ViewAccountModal from '../../components/admin/channels/ViewAccountModal.js'
import EditAccountModal from '../../components/admin/channels/EditAccountModal.js'
import ProviderTabs, { type TabProvider } from '../../components/admin/channels/ProviderTabs.js'
import OAuthChannelsPanel from '../../components/admin/channels/OAuthChannelsPanel.js'
import TwilioChannelsPanel from '../../components/admin/channels/TwilioChannelsPanel.js'
import { FormContainer } from '../../components/forms/layout/index.js'
import { api } from '../../api/client.js'
import { useAuthStore } from '../../store/authStore.js'
import { useToast } from '../../components/system/ToastProvider.js'
import { useChannelsOnboarding } from '../../hooks/useChannelsOnboarding.js'
import type {
  TwilioStatusResponse,
  ChannelProviderName,
  ConnectedAccount,
  ConnectedAccountsListResponse,
  Asset,
} from './channels-types.js'

const CHANNEL_PROVIDERS: ChannelProviderName[] = ['facebook', 'instagram', 'whatsapp', 'youtube', 'x']

export default function ChannelsPage(): JSX.Element {
  const toast = useToast()
  const [twilioStatus, setTwilioStatus] = useState<TwilioStatusResponse | null>(null)
  const [loading, setLoading] = useState(true)

  const [activeProvider, setActiveProvider] = useState<TabProvider>('facebook')

  // Asset selection modal state
  const [assetModalVisible, setAssetModalVisible] = useState(false)
  const [assetModalProvider, setAssetModalProvider] = useState<string>('')
  const [assetModalAssets, setAssetModalAssets] = useState<Asset[]>([])

  // Connected accounts list (per active provider) — the single source of
  // truth for whether a provider has any connections, driving both the
  // connect-prompt card visibility and the accounts table.
  const [accounts, setAccounts] = useState<ConnectedAccount[]>([])
  const [accountsLoading, setAccountsLoading] = useState(false)
  const [addAccountModalVisible, setAddAccountModalVisible] = useState(false)
  const [viewAccount, setViewAccount] = useState<ConnectedAccount | null>(null)
  const [editAccount, setEditAccount] = useState<ConnectedAccount | null>(null)

  const tenantSlug = useAuthStore((s) => s.user?.tenantSlug ?? '')
  const apiBase = (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? ''
  const twilioWebhookUrl = `${apiBase}/webhooks/twilio/${tenantSlug}`

  const fetchTwilioStatus = useCallback(async (): Promise<void> => {
    try {
      const data = await api.get<TwilioStatusResponse>('/admin/channels/twilio/status')
      setTwilioStatus(data)
    } catch (e) {
      toast.error('Failed to load Twilio status', e instanceof Error ? e.message : 'Failed to load Twilio status.')
    }
  }, [toast])

  const fetchAccounts = useCallback(async (provider: ChannelProviderName): Promise<void> => {
    // eslint-disable-next-line no-console
    console.debug('[channels] fetchAccounts: start', { provider })
    setAccountsLoading(true)
    try {
      const data = await api.get<ConnectedAccountsListResponse>(`/admin/channels/${provider}/accounts`)
      // eslint-disable-next-line no-console
      console.debug('[channels] fetchAccounts: success', { provider, count: data.accounts.length })
      setAccounts(data.accounts)
    } catch (e) {
      setAccounts([])
      toast.error('Failed to load connected accounts', e instanceof Error ? e.message : 'Failed to load connected accounts.')
    } finally {
      setAccountsLoading(false)
    }
  }, [toast])

  // Refetches whatever is relevant to the currently-active tab. Centralizes
  // the fetch-after-mutation pattern that was previously repeated at every
  // call site (disconnect, onboarding complete, asset selection, etc).
  const refreshAll = useCallback(async (): Promise<void> => {
    // eslint-disable-next-line no-console
    console.debug('[channels] refreshAll: called', { activeProvider })
    if (activeProvider !== 'twilio') {
      await fetchAccounts(activeProvider)
    }
  }, [activeProvider, fetchAccounts])

  // Deliberately runs once on mount only — subsequent refetches go through
  // refreshAll() or the targeted fetch*() calls, not this effect.
  useEffect(() => {
    setLoading(true)
    void fetchTwilioStatus().finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (activeProvider === 'twilio') return
    void fetchAccounts(activeProvider)
  }, [activeProvider, fetchAccounts])

  const handleDisconnectAccount = useCallback(async (account: ConnectedAccount): Promise<void> => {
    await api.delete(`/admin/channels/${account.provider}/${account.accountKey}`)
    await refreshAll()
  }, [refreshAll])

  const handleDeleteAccount = useCallback(async (account: ConnectedAccount): Promise<void> => {
    await api.delete(`/admin/channels/${account.provider}/${encodeURIComponent(account.accountKey)}/purge`)
    await refreshAll()
  }, [refreshAll])

  const handlePendingAssetSelection = useCallback((provider: string, assets: Asset[]) => {
    setAssetModalProvider(provider)
    setAssetModalAssets(assets)
    setAssetModalVisible(true)
  }, [])

  // Drives the "Add Account" modal's OAuth branch — same popup+postMessage
  // flow used by ChannelProviderCard, hoisted here since the modal isn't
  // tied to a single asset/card.
  const addAccountOnboarding = useChannelsOnboarding({
    provider: activeProvider === 'twilio' ? 'facebook' : activeProvider,
    onComplete: refreshAll,
    onPendingAssetSelection: (assets) => handlePendingAssetSelection(activeProvider, assets),
  })

  const handleAssetSelect = useCallback(async (assetId: string, assetName: string): Promise<void> => {
    await api.post('/admin/channels/select-asset', {
      provider: assetModalProvider,
      assetId,
      assetName,
    })
    await refreshAll()
  }, [assetModalProvider, refreshAll])

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="pi pi-spinner pi-spin text-3xl text-[var(--kc3-accent)]" />
          <span className="text-sm font-medium text-gray-600">Loading channel status…</span>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <FormContainer className="w-full px-6 py-8">
        {/* Page header — matches the eyebrow/title/description pattern used across admin pages. */}
        <div className="rounded-sm border border-gray-200 bg-white shadow-sm p-6">
          <div className="flex items-start gap-3.5">
            <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--kc3-accent)]/10 text-[var(--kc3-accent)]">
              <i className="pi pi-comment text-lg" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--kc3-accent)]">
                CHANNELS
              </span>
              <h1 className="mt-0.5 text-2xl font-bold tracking-tight text-gray-900">Channels</h1>
              <p className="mt-1 text-sm text-gray-500">
                Connect your messaging channels to enable two-way communication across platforms.
              </p>
            </div>
          </div>
        </div>

        <ProviderTabs
          providers={CHANNEL_PROVIDERS}
          activeProvider={activeProvider}
          onSelect={setActiveProvider}
        />

        {activeProvider !== 'twilio' ? (
          <OAuthChannelsPanel
            provider={activeProvider}
            accounts={accounts}
            accountsLoading={accountsLoading}
            onCardComplete={refreshAll}
            onPendingAssetSelection={handlePendingAssetSelection}
            onDisconnectAccount={handleDisconnectAccount}
            onReconnectAccount={() => void addAccountOnboarding.launch()}
            onAddAccount={() => setAddAccountModalVisible(true)}
            onViewAccount={setViewAccount}
            onEditAccount={setEditAccount}
            onDeleteAccount={handleDeleteAccount}
          />
        ) : (
          <TwilioChannelsPanel
            status={twilioStatus}
            webhookUrl={twilioWebhookUrl}
            onChanged={() => void fetchTwilioStatus()}
          />
        )}

        <AssetSelectionModal
          visible={assetModalVisible}
          provider={assetModalProvider}
          assets={assetModalAssets}
          onSelect={handleAssetSelect}
          onCancel={() => setAssetModalVisible(false)}
        />

        {activeProvider !== 'twilio' && (
          <AddAccountModal
            visible={addAccountModalVisible}
            provider={activeProvider}
            onCancel={() => setAddAccountModalVisible(false)}
            onOAuthLaunch={() => void addAccountOnboarding.launch()}
            onManualAdded={async () => {
              await fetchAccounts(activeProvider)
            }}
          />
        )}

        {activeProvider !== 'twilio' && (
          <ViewAccountModal
            visible={viewAccount !== null}
            provider={activeProvider}
            account={viewAccount}
            onClose={() => setViewAccount(null)}
          />
        )}

        {activeProvider !== 'twilio' && (
          <EditAccountModal
            visible={editAccount !== null}
            provider={activeProvider}
            account={editAccount}
            onCancel={() => setEditAccount(null)}
            onSaved={async () => {
              await fetchAccounts(activeProvider)
            }}
          />
        )}
      </FormContainer>
    </div>
  )
}
